/**
 * SupplierPicker — partner dropdown for the inline expanded row.
 *
 * Calls `GET /api/admin/partners/for-product?category=...&product=...`
 * and ranks the matching partners on top, then falls back to the
 * full directory below a separator so the admin can override.
 *
 * Per the operator brief: ALWAYS ask for confirmation — no auto-pick.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SupplierPicker = ({ category, product, currentPartnerId, onChange }) => {
  const [matches, setMatches] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(currentPartnerId || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/partners/for-product`,
          { params: { category, product }, withCredentials: true },
        );
        if (cancelled) return;
        setMatches(res.data?.matches || []);
        setAllPartners(res.data?.all_partners || []);
      } catch {
        if (!cancelled) {
          setMatches([]);
          setAllPartners([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category, product]);

  // Notify parent on any selection change.
  const handleSelect = (id) => {
    setSelectedId(id);
    const pool = [...matches, ...allPartners];
    const found = pool.find((p) => p.id === id);
    if (found) onChange?.(found);
    else onChange?.(null);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading partners…
      </div>
    );
  }

  // Build a deduped list: matches first, then any partner not in matches.
  const matchIds = new Set(matches.map((m) => m.id));
  const others = allPartners.filter((p) => !matchIds.has(p.id));

  return (
    <div data-testid="supplier-picker">
      <select
        value={selectedId}
        onChange={(e) => handleSelect(e.target.value)}
        className="w-full text-sm border border-[#6FBF73]/40 rounded-md px-2.5 py-2 focus:border-[#6FBF73] focus:ring-1 focus:ring-[#6FBF73]/40 bg-white"
        data-testid="supplier-picker-select"
      >
        <option value="">— Pick a supplier —</option>
        {matches.length > 0 && (
          <optgroup label={`Recommended for ${product || 'this product'}`}>
            {matches.map((p) => (
              <option key={p.id} value={p.id}>
                ★ {p.name} — {p.email}
              </option>
            ))}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label="All other partners">
            {others.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.email}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {matches.length === 0 && product && (
        <p className="mt-1 text-[10px] text-orange-600">
          No partner mapped for "{product}". Pick from the full directory above.
        </p>
      )}
      {matches.length > 1 && (
        <p className="mt-1 text-[10px] text-[#1F3D2C]/60">
          {matches.length} recommended partners. Operator must confirm pick.
        </p>
      )}
    </div>
  );
};

export default SupplierPicker;
